import { useState, useCallback } from "react";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { DateRange } from "react-day-picker";
import { Layer, Map as MapComponent, Source } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useStore } from "@/store/store";
import { heatmapLayer } from "@/components/map/heatmap-layer";
import { useAnalyticsHeatmap } from "@/api/v0/analytics";
import { ProOnlyGuard } from "@/components/pro-only-guard";
import { 
  Loader2, 
  Map, 
  Calendar, 
  Activity, 
  TrendingUp, 
  BarChart3,
  RefreshCw,
  Info
} from "lucide-react";
import { addDays, differenceInDays, format } from "date-fns";
import { toast } from "sonner";
import MovementEvents from "@/components/map/movement-events";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

export const HeatmapHistoryRoute = () => {
  const mapBounds = useStore(useCallback((state) => state.mapBounds, []));
  const mapZoom = useStore(useCallback((state) => state.mapZoom, []));
  const mapStyle = useStore(useCallback((state) => state.mapStyleUrl, []));

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // Default to last 7 days
    to: new Date(),
  });

  const { data: heatmapData, refetch, isRefetching, isLoading } = useAnalyticsHeatmap(5, dateRange);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) {
      setDateRange(range);
      return;
    }

    const daysDifference = differenceInDays(range.to, range.from);
    if (daysDifference > 30) {
      toast.error("Date range cannot exceed 30 days");
      // Set the 'to' date to 30 days after the 'from' date
      setDateRange({
        from: range.from,
        to: addDays(range.from, 30)
      });
      return;
    }

    setDateRange(range);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await refetch({ throwOnError: true });
  };

  const hasData = heatmapData && heatmapData.features && heatmapData.features.length > 0;

  return (
    <ProOnlyGuard
      title="Heatmap Analytics"
      description="Visualize drone activity patterns with interactive heatmap analytics. Available for Pro subscribers."
    >
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Heatmap History", href: "/heatmap-history" },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Heatmap History</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Visualize drone activity density over time with interactive heatmaps
            </p>
          </div>
          <Badge variant="outline" className="w-fit">
            <Map className="h-3 w-3 mr-1" />
            Spatial Analytics
          </Badge>
        </div>

        {/* Controls Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Date Range Selection
            </CardTitle>
            <CardDescription>
              Select a date range to analyze drone activity patterns (maximum 30 days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                <div className="flex-1">
                  <DateRangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    disabled={isRefetching || isLoading}
                  />
                </div>
                <Button 
                  type="submit" 
                  variant="default" 
                  disabled={isRefetching || isLoading}
                  className="w-full sm:w-auto"
                >
                  {isRefetching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Generate Heatmap
                    </>
                  )}
                </Button>
              </div>
              
              {dateRange?.from && dateRange?.to && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4" />
                  <span>
                    Analyzing {differenceInDays(dateRange.to, dateRange.from) + 1} days from {format(dateRange.from, 'MMM dd')} to {format(dateRange.to, 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Map Container */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Activity Heatmap
            </CardTitle>
            <CardDescription>
              {hasData 
                ? `Showing activity density for ${heatmapData.features.length} data points`
                : "No heatmap data available for the selected date range"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="relative h-[600px] w-full">
              <MapComponent
                id="map"
                initialViewState={{
                  longitude: mapBounds?.[0] ?? -0.1276,
                  latitude: mapBounds?.[1] ?? 51.5074,
                  zoom: mapZoom ?? 10,
                }}
                style={{ width: "100%", height: "100%" }}
                mapStyle={mapStyle}
                maxZoom={15}
                minZoom={2}
                maxPitch={0}
              >
                <MovementEvents />
                {hasData && (
                  <Source 
                    type="geojson" 
                    data={heatmapData}
                  >
                    <Layer {...heatmapLayer} />
                  </Source>
                )}
              </MapComponent>

              {/* Loading Overlay */}
              {(isLoading || isRefetching) && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Generating heatmap...</p>
                  </div>
                </div>
              )}

              {/* No Data State */}
              {!isLoading && !isRefetching && !hasData && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Empty>
                    <EmptyMedia variant="icon">
                      <Activity className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyTitle>No Activity Data</EmptyTitle>
                      <EmptyDescription>
                        No drone activity found for the selected date range. Try adjusting your filters or selecting a different time period.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Information Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Data Points</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{heatmapData?.features?.length ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                Activity points analyzed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Date Range</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {dateRange?.from && dateRange?.to ? (
                <div className="text-2xl font-bold">{differenceInDays(dateRange.to, dateRange.from) + 1}</div>
              ) : (
                <div className="text-2xl font-bold">--</div>
              )}
              <p className="text-xs text-muted-foreground">
                Days analyzed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading || isRefetching ? (
                <Badge variant="secondary">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Processing
                </Badge>
              ) : hasData ? (
                <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Data Available
                </Badge>
              ) : (
                <Badge variant="outline">
                  No Data
                </Badge>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Heatmap status
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Understanding Heatmaps</p>
              <p>
                Heatmaps show the density of drone activity across your coverage area. Warmer colors indicate higher activity levels. 
                Use the date range picker to analyze different time periods and identify patterns in drone behavior.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </main>
    </ProOnlyGuard>
  );
}; 